import { defineStore } from '@warehouse/shared/util';
import { StateCreator } from 'zustand/vanilla';
import { getQuickbaseSingleton } from '@warehouse/quickbase';
import { NavbarType, TabType } from '../quickbase-types';
import { createCommonQuickbaseSlice, QuickbaseCommonSlice, resolveFrameUrl } from '../quickbase.common';
import { quickbaseTabServiceSingletons } from '../quickbase-tab-service';
import { IframeEmbedParams } from '../quickbase-host-bridge/iframe-url-state';

export type QuickbaseNavbarStore = Omit<QuickbaseCommonSlice, 'actions'> & {
	actions: QuickbaseCommonSlice['actions'] & {
		loadEmbedUrl(embedParams?: IframeEmbedParams): Promise<void>;
	};
};

export type QuickbaseTabStore = Omit<QuickbaseCommonSlice, 'actions'> & {
	actions: QuickbaseCommonSlice['actions'] & {
		loadEmbedUrl(titleUuid: string, embedParams?: IframeEmbedParams): Promise<void>;
	};
};

export function quickbaseNavbarCreateStoreFactory(navType: NavbarType) {
	const createStore: StateCreator<QuickbaseNavbarStore> = (set, get, store) => {
		const quickbaseNavbarService = getQuickbaseSingleton(navType).get();

		const commonQuickbaseSlice = createCommonQuickbaseSlice(set, get, store);

		return {
			...commonQuickbaseSlice,
			actions: {
				...commonQuickbaseSlice.actions,
				async loadEmbedUrl(embedParams?: IframeEmbedParams) {
					set({ isLoading: true, error: null });

					try {
						if (!quickbaseNavbarService.validateConfiguration()) {
							set({
								isLoading: false,
								error: `${navType} Dashboard configuration is invalid`,
								embedUrl: null,
								frameUrl: null,
							});
							return;
						}

						const embedUrl = quickbaseNavbarService.buildEmbedUrl(embedParams);

						set({ isLoading: false, embedUrl, frameUrl: resolveFrameUrl(embedUrl), error: null });
					} catch (error) {
						set({
							isLoading: false,
							error: error instanceof Error ? error.message : 'Failed to load embed URL',
							embedUrl: null,
							frameUrl: null,
						});
					}
				},
			},
		};
	};
	return createStore;
}

export function quickbaseTabCreateStoreFactory(tabType: TabType) {
	const createStore: StateCreator<QuickbaseTabStore> = (set, get, store) => {
		const quickbaseTabService = quickbaseTabServiceSingletons[tabType].get();

		const commonQuickbaseSlice = createCommonQuickbaseSlice(set, get, store);

		return {
			...commonQuickbaseSlice,
			actions: {
				...commonQuickbaseSlice.actions,
				async loadEmbedUrl(titleUuid: string, embedParams?: IframeEmbedParams) {
					set({ isLoading: true, error: null });

					try {
						if (!quickbaseTabService.validateConfiguration()) {
							set({
								isLoading: false,
								error: `${tabType} configuration is invalid`,
								embedUrl: null,
								frameUrl: null,
							});
							return;
						}

						const embedUrl = quickbaseTabService.buildEmbedUrl(titleUuid, embedParams);

						set({
							isLoading: false,
							embedUrl,
							frameUrl: resolveFrameUrl(embedUrl),
							error: null,
						});
					} catch (error) {
						set({
							isLoading: false,
							error: error instanceof Error ? error.message : 'Failed to load embed URL',
							embedUrl: null,
							frameUrl: null,
						});
					}
				},
			},
		};
	};
	return createStore;
}

// Create separate stores for Utilities and Dashboard nav items
export const [UtilitiesProvider, useUtilitiesStore] = defineStore(
	quickbaseNavbarCreateStoreFactory(NavbarType.Utilities),
);

export const [DashboardProvider, useDashboardStore] = defineStore(
	quickbaseNavbarCreateStoreFactory(NavbarType.Dashboard),
);

export const [AdminProvider, useAdminStore] = defineStore(quickbaseNavbarCreateStoreFactory(NavbarType.Admin));

export const [OrdersProvider, useOrdersStore] = defineStore(quickbaseNavbarCreateStoreFactory(NavbarType.Orders));
export const [InventoryProvider, useInventoryStore] = defineStore(
	quickbaseNavbarCreateStoreFactory(NavbarType.Inventory),
);

export const [NxTensionStoreProvider, useNxTensionStore] = defineStore<QuickbaseTabStore>(
	quickbaseTabCreateStoreFactory(TabType.NxTension),
);

export const [NxOrdersStoreProvider, useNxOrdersStore] = defineStore<QuickbaseTabStore>(
	quickbaseTabCreateStoreFactory(TabType.NxOrders),
);

export const [NxInventoryStoreProvider, useNxInventoryStore] = defineStore<QuickbaseTabStore>(
	quickbaseTabCreateStoreFactory(TabType.NxInventory),
);

export const quickbaseNavbarStoreSelector = {
	actions: (state: QuickbaseNavbarStore) => state.actions,
	isLoading: (state: QuickbaseNavbarStore) => state.isLoading,
	isAuthenticated: (state: QuickbaseNavbarStore) => state.isAuthenticated,
	authFlow: (state: QuickbaseNavbarStore) => state.authFlow,
	error: (state: QuickbaseNavbarStore) => state.error,
	embedUrl: (state: QuickbaseNavbarStore) => state.embedUrl,
	frameUrl: (state: QuickbaseNavbarStore) => state.frameUrl,
	showAuthOverlay: (state: QuickbaseNavbarStore) => state.showAuthOverlay,
};

export const quickbaseTabStoreSelector = {
	isLoading: (state: QuickbaseTabStore) => state.isLoading,
	isAuthenticated: (state: QuickbaseTabStore) => state.isAuthenticated,
	authFlow: (state: QuickbaseTabStore) => state.authFlow,
	error: (state: QuickbaseTabStore) => state.error,
	embedUrl: (state: QuickbaseTabStore) => state.embedUrl,
	frameUrl: (state: QuickbaseTabStore) => state.frameUrl,
	showAuthOverlay: (state: QuickbaseTabStore) => state.showAuthOverlay,
	actions: (state: QuickbaseTabStore) => state.actions,
};
